export const NAVIGATION_MODES = [
    { id: 'walking', label: 'Пешком', iconUrl: '/images/03_travel/travel_type/person0.png' },
    { id: 'driving', label: 'На машине', iconUrl: '/images/03_travel/travel_type/car1.png' },
    { id: 'cycling', label: 'Велосипедом', iconUrl: '/images/03_travel/travel_type/bicycle0.png' },
];
const rad = Math.PI / 180;
const GPS_FIX_MAX_AGE = 15000;
export function navigationDistance(a, b) {
    const h = Math.sin((b[1] - a[1]) * rad / 2) ** 2
        + Math.cos(a[1] * rad) * Math.cos(b[1] * rad) * Math.sin((b[0] - a[0]) * rad / 2) ** 2;
    return 12742000 * Math.asin(Math.sqrt(Math.min(1, h)));
}
export function formatNavigationDistance(meters) {
    return meters < 1000 ? `${Math.max(0, Math.round(meters / 10) * 10)} м` : `${(meters / 1000).toFixed(1).replace('.', ',')} км`;
}
export function formatNavigationDuration(seconds) {
    const minutes = Math.max(1, Math.ceil(seconds / 60));
    return minutes < 60 ? `${minutes} мин` : `${Math.floor(minutes / 60)} ч${minutes % 60 ? ` ${minutes % 60} мин` : ''}`;
}
const validCoordinate = (point) => Array.isArray(point)
    && point.length === 2 && point.every(value => typeof value === 'number' && Number.isFinite(value))
    && Math.abs(point[0]) <= 180 && Math.abs(point[1]) <= 90;
export async function requestNavigationRoute(options) {
    var _a, _b, _c, _d, _e;
    if (!validCoordinate(options.origin) || !validCoordinate(options.destination))
        throw new Error('Не удалось определить координаты маршрута.');
    if (!options.accessToken)
        throw new Error('Сервис маршрутов не настроен.');
    if (!NAVIGATION_MODES.some(mode => mode.id === options.mode))
        throw new Error('Выберите способ передвижения.');
    const profile = options.mode === 'driving' ? 'driving-traffic' : options.mode;
    const coordinates = `${options.origin.join(',')};${options.destination.join(',')}`;
    const query = new URLSearchParams({ access_token: options.accessToken, geometries: 'geojson', overview: 'full', steps: 'true', language: 'ru', alternatives: 'false' });
    const response = await fetch(`https://api.mapbox.com/directions/v5/mapbox/${profile}/${coordinates}?${query}`, { signal: options.signal });
    if (!response.ok)
        throw new Error(response.status === 429 ? 'Сервис маршрутов занят. Попробуйте через минуту.' : 'Не удалось построить маршрут. Проверьте интернет и повторите.');
    const data = await response.json();
    const route = (_a = data.routes) === null || _a === void 0 ? void 0 : _a[0];
    if (data.code !== 'Ok' || !route)
        throw new Error('Для выбранного способа передвижения маршрут не найден.');
    if (((_b = route.geometry) === null || _b === void 0 ? void 0 : _b.type) !== 'LineString' || !Array.isArray(route.geometry.coordinates)
        || route.geometry.coordinates.length < 2 || !route.geometry.coordinates.every(validCoordinate)
        || !Number.isFinite(route.distance) || !Number.isFinite(route.duration) || route.distance < 0 || route.duration < 0) {
        throw new Error('Сервис вернул неполный маршрут. Попробуйте ещё раз.');
    }
    let start = 0;
    const steps = [];
    for (const leg of route.legs || [])
        for (const step of leg.steps || []) {
            const distance = Math.max(0, Number(step.distance) || 0);
            steps.push({ start, distance, duration: Math.max(0, Number(step.duration) || 0),
                instruction: String(((_c = step.maneuver) === null || _c === void 0 ? void 0 : _c.instruction) || 'Продолжайте по маршруту'),
                type: String(((_d = step.maneuver) === null || _d === void 0 ? void 0 : _d.type) || ''), modifier: String(((_e = step.maneuver) === null || _e === void 0 ? void 0 : _e.modifier) || ''), name: String(step.name || '') });
            start += distance;
        }
    return { coordinates: route.geometry.coordinates, distance: route.distance, duration: route.duration, steps };
}
export function navigationProgress(route, fix, previousTravelled = 0) {
    var _a;
    const lengths = [0];
    for (let i = 1; i < route.coordinates.length; i++)
        lengths[i] = lengths[i - 1] + navigationDistance(route.coordinates[i - 1], route.coordinates[i]);
    const total = lengths[lengths.length - 1] || 1;
    const previous = previousTravelled * total / (route.distance || total);
    let best = { coordinate: route.coordinates[0], segment: 0, travelled: 0, distance: Infinity, score: Infinity };
    const scale = Math.max(0.00001, Math.cos(fix.coordinate[1] * rad));
    for (let i = 0; i < route.coordinates.length - 1; i++) {
        const a = route.coordinates[i], b = route.coordinates[i + 1];
        const dx = (b[0] - a[0]) * scale, dy = b[1] - a[1];
        const ratio = Math.max(0, Math.min(1, ((fix.coordinate[0] - a[0]) * scale * dx + (fix.coordinate[1] - a[1]) * dy) / (dx * dx + dy * dy || 1)));
        const coordinate = [a[0] + (b[0] - a[0]) * ratio, a[1] + (b[1] - a[1]) * ratio];
        const travelled = lengths[i] + (lengths[i + 1] - lengths[i]) * ratio;
        if (previous > 0 && travelled < previous - 40)
            continue;
        const distance = navigationDistance(fix.coordinate, coordinate);
        // Prefer the nearby portion of the route at crossings, rather than jumping
        // ahead to a later pass through the same coordinates.
        const score = distance + Math.abs(travelled - previous) * 0.003;
        if (score < best.score)
            best = { coordinate, segment: i, travelled, distance, score };
    }
    const travelled = Math.min(route.distance, best.travelled / total * route.distance);
    const remaining = Math.max(0, route.distance - travelled);
    const step = route.steps.find((entry, index) => index > 0 && entry.start > travelled + 7) || route.steps[route.steps.length - 1] || null;
    let duration = route.duration * (route.distance ? remaining / route.distance : 0);
    if (route.steps.length)
        duration = route.steps.reduce((sum, entry) => sum + entry.duration * (entry.distance ? Math.max(0, Math.min(1, (entry.start + entry.distance - travelled) / entry.distance)) : 0), 0);
    const a = route.coordinates[best.segment], b = route.coordinates[best.segment + 1];
    const bearing = (Math.atan2(Math.sin((b[0] - a[0]) * rad) * Math.cos(b[1] * rad), Math.cos(a[1] * rad) * Math.sin(b[1] * rad) - Math.sin(a[1] * rad) * Math.cos(b[1] * rad) * Math.cos((b[0] - a[0]) * rad)) / rad + 360) % 360;
    return { coordinate: best.coordinate, segment: best.segment, travelled, offRoute: best.distance, remaining, duration,
        maneuverDistance: Math.max(0, ((_a = step === null || step === void 0 ? void 0 : step.start) !== null && _a !== void 0 ? _a : route.distance) - travelled), step, bearing,
        arrived: fix.accuracy <= 35 && remaining < 60 && navigationDistance(fix.coordinate, route.coordinates[route.coordinates.length - 1]) <= 25 };
}
// Shared lifecycle for browser and native GPS. No trip data or completion is written.
export class NavigationSession {
    constructor(options) {
        this.options = options;
        this.state = { status: 'idle', mode: 'walking', route: null, fix: null, progress: null, error: '', rerouting: false, gpsLost: false, starting: false };
        this.listeners = new Set();
        this.controller = null;
        this.removeWatch = null;
        this.timer = null;
        this.revision = 0;
        this.disposed = false;
        this.offRouteCount = 0;
        this.lastReroute = 0;
        this.trackingStartedAt = 0;
    }
    subscribe(listener) {
        this.listeners.add(listener);
        listener(this.state);
        return () => { this.listeners.delete(listener); };
    }
    patch(patch) {
        if (this.disposed)
            return;
        this.state = { ...this.state, ...patch };
        this.listeners.forEach(listener => listener(this.state));
    }
    async chooseMode(mode) {
        var _a;
        this.stopTracking();
        (_a = this.controller) === null || _a === void 0 ? void 0 : _a.abort();
        const revision = ++this.revision;
        this.controller = new AbortController();
        const controller = this.controller;
        this.patch({ status: 'loading', mode, route: null, progress: null, fix: null, error: '', rerouting: false, gpsLost: false, starting: false });
        let timeout;
        let cancelPosition;
        try {
            const fix = await Promise.race([this.options.getPosition(), new Promise((_, reject) => {
                    cancelPosition = () => reject(new Error('Расчёт отменён.'));
                    controller.signal.addEventListener('abort', cancelPosition, { once: true });
                    timeout = setTimeout(() => reject(new Error('Не удалось определить местоположение. Проверьте GPS и повторите.')), 20000);
                })]);
            if (revision !== this.revision || this.disposed)
                return;
            clearTimeout(timeout);
            const route = await this.fetchRoute(fix, mode, controller);
            if (revision !== this.revision || this.disposed)
                return;
            this.patch({ status: 'preview', route, fix, progress: navigationProgress(route, fix) });
        }
        catch (error) {
            if (revision === this.revision && !this.disposed)
                this.patch({ status: 'idle', error: error instanceof Error ? error.message : 'Не удалось построить маршрут.' });
        }
        finally {
            clearTimeout(timeout);
            if (cancelPosition)
                controller.signal.removeEventListener('abort', cancelPosition);
        }
    }
    async fetchRoute(fix, mode, controller) {
        const timeout = setTimeout(() => controller.abort(), 20000);
        try {
            return await (this.options.requestRoute || requestNavigationRoute)({ accessToken: this.options.accessToken, origin: fix.coordinate,
                destination: this.options.destination, mode, signal: controller.signal });
        }
        catch (error) {
            if (controller.signal.aborted)
                throw new Error('Не удалось получить маршрут. Проверьте интернет и повторите.');
            throw error;
        }
        finally {
            clearTimeout(timeout);
        }
    }
    async start() {
        if (this.state.status !== 'preview' || !this.state.route || this.state.starting)
            return;
        const revision = this.revision;
        this.trackingStartedAt = Date.now();
        this.patch({ status: 'navigating', starting: true, error: '', gpsLost: false });
        this.timer = setInterval(() => {
            if (!this.hasRecentFix())
                this.patch({ gpsLost: true });
        }, 5000);
        let timeout;
        let expired = false;
        try {
            const subscription = this.options.watchPosition(fix => this.updatePosition(fix), () => this.patch({ gpsLost: true }))
                .then(remove => { if (expired)
                remove(); return remove; });
            const remove = await Promise.race([subscription, new Promise((_, reject) => {
                    timeout = setTimeout(() => {
                        expired = true;
                        reject(new Error('Не удалось включить геолокацию. Проверьте GPS и повторите.'));
                    }, 20000);
                })]);
            if (this.disposed || revision !== this.revision || !this.isNavigating())
                remove();
            else {
                this.removeWatch = remove;
                this.patch({ starting: false });
            }
        }
        catch (error) {
            if (revision !== this.revision || this.disposed)
                return;
            this.stopTracking();
            this.patch({ status: 'preview', starting: false, error: error instanceof Error ? error.message : 'Не удалось включить геолокацию.' });
        }
        finally {
            clearTimeout(timeout);
        }
    }
    updatePosition(fix) {
        var _a, _b;
        if (this.disposed || this.state.status !== 'navigating' || !this.state.route)
            return;
        if (!validCoordinate(fix.coordinate) || !Number.isFinite(fix.accuracy) || fix.accuracy < 0 || fix.accuracy > 70
            || !Number.isFinite(fix.timestamp) || Date.now() - fix.timestamp > GPS_FIX_MAX_AGE) {
            // A single poor reading must not discard a recent reliable location.
            if (!this.hasRecentFix())
                this.patch({ gpsLost: true });
            return;
        }
        const progress = navigationProgress(this.state.route, fix, (_a = this.state.progress) === null || _a === void 0 ? void 0 : _a.travelled);
        const offRoute = progress.offRoute > Math.max(this.state.mode === 'driving' ? 50 : 35, fix.accuracy * 2);
        this.offRouteCount = offRoute ? this.offRouteCount + 1 : 0;
        this.patch({ fix, progress, gpsLost: false });
        if (progress.arrived) {
            ++this.revision;
            (_b = this.controller) === null || _b === void 0 ? void 0 : _b.abort();
            this.stopTracking();
            this.patch({ status: 'arrived', starting: false, rerouting: false, error: '' });
        }
        else if (this.offRouteCount >= 3 && !this.state.rerouting && Date.now() - this.lastReroute >= 20000)
            void this.reroute(fix);
    }
    async reroute(fix) {
        var _a;
        this.lastReroute = Date.now();
        const revision = this.revision;
        (_a = this.controller) === null || _a === void 0 ? void 0 : _a.abort();
        this.controller = new AbortController();
        this.patch({ rerouting: true, error: '' });
        try {
            const route = await this.fetchRoute(fix, this.state.mode, this.controller);
            if (revision !== this.revision || this.disposed)
                return;
            this.offRouteCount = 0;
            const currentFix = this.state.fix || fix;
            this.patch({ route, progress: navigationProgress(route, currentFix), rerouting: false });
        }
        catch {
            if (revision === this.revision && !this.disposed)
                this.patch({ rerouting: false, error: 'Пересчитать маршрут не удалось. Проверьте интернет.' });
        }
    }
    stopTracking() {
        var _a;
        (_a = this.removeWatch) === null || _a === void 0 ? void 0 : _a.call(this);
        this.removeWatch = null;
        if (this.timer)
            clearInterval(this.timer);
        this.timer = null;
        this.offRouteCount = 0;
    }
    hasRecentFix() {
        var _a;
        // Give a newly started watcher time to replace the route preview's fix.
        return Date.now() - Math.max(this.trackingStartedAt, ((_a = this.state.fix) === null || _a === void 0 ? void 0 : _a.timestamp) || 0) <= GPS_FIX_MAX_AGE;
    }
    isNavigating() { return this.state.status === 'navigating'; }
    dispose() {
        var _a;
        this.disposed = true;
        ++this.revision;
        (_a = this.controller) === null || _a === void 0 ? void 0 : _a.abort();
        this.stopTracking();
        this.listeners.clear();
    }
}
